import { useEffect, useState } from "react";
import { todayISO } from "@/lib/utils";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { handleApiError } from "@/services/api";
import type { ApplyCreditResult } from "@/types/models";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const inr = (n: number) => "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });

export interface CashParty {
  id: number;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** "payment" for a supplier, "receipt" for a buyer — only the wording differs. */
  kind: "payment" | "receipt";
  parties: CashParty[];
  onSubmit: (data: { party_id: number; date: string; amount: number; notes?: string; local_id: string }) => Promise<ApplyCreditResult | null>;
  /** Called after a successful save so the page can reload its lists. */
  onSaved: () => void;
}

/**
 * Record Payment (to a supplier) / Record Receipt (from a buyer).
 *
 * CASH ONLY, and the operator never picks a bill: the money settles that
 * party's open bills oldest-first by bill date, and whatever is left is held
 * as credit for the next bill. Bank, UPI and cheque money comes in through a
 * statement upload, so it is never typed here and counted twice.
 */
export default function RecordCashModal({ isOpen, onClose, kind, parties, onSubmit, onSaved }: Props) {
  const [partyId, setPartyId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ amount: number; settled: ApplyCreditResult | null } | null>(null);
  // One id per opening of the form: a retried request after a network blip
  // is refused by the unique local_id index instead of paying twice.
  const [localId, setLocalId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (isOpen) {
      setPartyId("");
      setDate(todayISO());
      setAmount("");
      setNotes("");
      setError("");
      setResult(null);
      setLocalId(crypto.randomUUID());
    }
  }, [isOpen]);

  const party = kind === "payment" ? "Supplier" : "Buyer";
  const docs = kind === "payment" ? "bills" : "invoices";
  const title = kind === "payment" ? "Record Payment" : "Record Receipt";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!partyId) return setError(`Choose a ${party.toLowerCase()}.`);
    if (!(amt > 0)) return setError("Enter an amount above zero.");
    setSaving(true);
    setError("");
    try {
      const settled = await onSubmit({ party_id: parseInt(partyId), date, amount: amt, notes: notes || undefined, local_id: localId });
      setResult({ amount: amt, settled });
      onSaved();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        {/* pr-12 clears the Modal's close button (absolute, 44px, 24px in):
            without it the × sat on the end of this line (W6-1). */}
        <h3 className="mb-1 pr-12 text-lg font-semibold text-gray-800 dark:text-white/90">{title}</h3>
        <p className="mb-4 pr-12 text-xs text-gray-500 dark:text-gray-400">
          Cash. Settles the oldest {docs} first; anything left is held as credit for the next one.
          Bank, UPI and cheque {kind === "payment" ? "payments" : "receipts"} come in by uploading the bank statement (Finance → Bank).
        </p>

        {result ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-700 dark:border-success-500/30 dark:bg-success-500/10 dark:text-success-400">
              <p className="font-medium">{inr(result.amount)} recorded.</p>
              {result.settled && (
                <p className="mt-1 text-xs">
                  {result.settled.cleared_count > 0 && `${result.settled.cleared_count} ${docs.slice(0, -1)}${result.settled.cleared_count === 1 ? "" : "s"} settled. `}
                  {result.settled.partial_count > 0 && `${result.settled.partial_count} part-paid. `}
                  {result.settled.leftover_credit > 0.005 && `${inr(result.settled.leftover_credit)} held as credit.`}
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="cashParty">{party} *</Label>
              <select id="cashParty" value={partyId} onChange={(e) => setPartyId(e.target.value)} className={selectClass}>
                <option value="">Select {party.toLowerCase()}</option>
                {parties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cashDate">Date *</Label>
                <Input id="cashDate" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="cashAmount">Amount (cash) *</Label>
                <Input
                  id="cashAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="cashNotes">Notes</Label>
              <Input id="cashNotes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : title}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
